import { useDispatch, useSelector } from "react-redux";
import { ReactNode, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { clearFilters } from "redux/SingleDataset/actions";
import { TabTypes } from "types";
import ExploreTabIcon from "assets/icons/ExploreTabIcon";
import DataTabIcon from "assets/icons/DataTabIcon";
import classNames from "classnames";
import styles from "./style.module.scss";
import VLPopover from "views/uikit/VLPopover";
import { getIsAddMediaEnabled } from "redux/Datasets/selectors";
import { useFeatureFlagService } from "../../../../hooks/useFeatureFlagService";
import {
  ADD_MEDIA,
  ADD_MEDIA_TAB,
  NEW_STATUS,
} from "../../../../helpers/constants/filters";
import { useUserPermission } from "../../../../hooks/useUserPermission";
import { NO_PERMISSION_TOOLTIP_TEXT } from "../../../../helpers/constants/tooltips";

interface TabProps {
  label: string;
  selected: boolean;
  onClick: () => void;
  icon: ReactNode;
  showBeta?: boolean;
  disabled: boolean;
  className?: string;
}

interface TabSwitchProps {
  showTabs: TabTypes[];
  dataset: any;
  className?: string;
}

export const Tab = ({
  icon,
  label,
  onClick,
  disabled,
  selected,
  className,
}: TabProps) => {
  const tabButtonContainer = classNames(
    styles.tabButton,
    "tabButton",
    {
      [styles.selected]: selected,
      [styles.disableTab]: disabled,
    },
    className,
  );

  return (
    <div
      className={tabButtonContainer}
      onClick={disabled ? undefined : onClick}
    >
      {icon}
      {label}
      {selected && (
        <div
          className={classNames(
            styles.selectedBorderBottom,
            "selectedBorderBottom",
          )}
        ></div>
      )}
    </div>
  );
};

type TabData = Pick<TabProps, "label" | "disabled" | "icon"> & {
  tooltip?: string;
  type: TabTypes;
};

const TabSwitch = ({ showTabs, dataset, className }: TabSwitchProps) => {
  const { datasetId, newDatasetId, enrichDatasetId, viewsDatasetId } =
    useParams();

  const initialTab = newDatasetId
    ? TabTypes.DATA_INGESTION
    : TabTypes.DATA_EXPLORATION;

  const [selectedTab, setSelectedTab] = useState<TabTypes>(initialTab); //does not contain the BOTH value

  const allowUpdateDataset = useSelector(getIsAddMediaEnabled);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handlePageClick = (value: TabTypes) => {
    if (selectedTab === value) return;
    const id = newDatasetId || enrichDatasetId || datasetId || viewsDatasetId;
    setSelectedTab(value);
    switch (value) {
      case TabTypes.DATA_EXPLORATION:
        navigate(`/dataset/${id}/data`);
        break;

      case TabTypes.DATA_INGESTION:
        dispatch(clearFilters() as any);
        const batchId = dataset?.current_batch_id;
        if (!dataset.creating && allowUpdateDataset) {
          navigate(`/dataset/${id}/update${batchId ? `/${batchId}` : ""}`);
        } else {
          navigate(`/dataset/${id}/create`);
        }
        break;
    }
  };

  const canAddMedia = useUserPermission("can_add_media");
  const displayNewStatus = useFeatureFlagService(NEW_STATUS).show;
  const { enabled: addMediaTabEnabled, reason: addMediaTabReason } =
    useFeatureFlagService(ADD_MEDIA_TAB);
  const addMediaEnabled =
    useFeatureFlagService(ADD_MEDIA).enabled || !canAddMedia;
  const disabledDataTab =
    (displayNewStatus ? !addMediaTabEnabled : !addMediaEnabled) || !canAddMedia;

  const tabs = useMemo<TabData[]>(() => {
    return [
      {
        type: TabTypes.DATA_INGESTION,
        label: "Data",
        icon: <DataTabIcon />,
        disabled: disabledDataTab,
        tooltip:
          addMediaTabReason ||
          (!canAddMedia ? NO_PERMISSION_TOOLTIP_TEXT : null),
      },
      {
        type: TabTypes.DATA_EXPLORATION,
        label: "Explore",
        icon: <ExploreTabIcon />,
      },
    ]
      .filter((tab) => tab && showTabs.includes(tab.type))
      .map((tab) => tab as TabData);
  }, [disabledDataTab, addMediaTabReason, canAddMedia, showTabs]);

  const filteredTabs = tabs.filter((tab) => showTabs.includes(tab.type));

  return (
    <div className={classNames(styles.pageSwitchContainer, className)}>
      {filteredTabs.map((tab) => {
        const selected = selectedTab === tab.type;
        return (
          <VLPopover
            placement={"bottom"}
            content={tab.disabled ? tab.tooltip : null}
          >
            <Tab
              key={tab.type}
              label={tab.label}
              selected={selected}
              disabled={tab.disabled}
              onClick={() => handlePageClick(tab.type)}
              icon={tab.icon}
            />
          </VLPopover>
        );
      })}
    </div>
  );
};

export default TabSwitch;
